import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Brain,
  Sparkles,
  FileText,
  ShieldCheck,
  Users,
  Calendar,
  ClipboardList,
  Stethoscope,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Clock,
  LayoutDashboard,
  Heart,
  Download,
  GraduationCap,
  Image,
  MessageCircle
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

function SectionTitle({ kicker, title, subtitle, center = true, dark = false }) {
  return (
    <div className={center ? "text-center max-w-3xl mx-auto mb-12" : "mb-8"}>
      {kicker && (
        <div className={`inline-flex items-center gap-2 font-semibold tracking-wide uppercase text-xs ${dark ? "text-indigo-300" : "text-indigo-700"}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${dark ? "bg-indigo-400" : "bg-indigo-600"}`} />
          {kicker}
        </div>
      )}
      <h2 className={`mt-2 text-3xl sm:text-4xl font-bold ${dark ? "text-white" : "text-gray-900"}`}>
        {title}
      </h2>
      {subtitle && (
        <p className={`mt-4 text-lg leading-relaxed ${dark ? "text-indigo-50/80" : "text-gray-600"}`}>{subtitle}</p>
      )}
    </div>
  );
}

export default function MarketingHome() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [faqOpen, setFaqOpen] = useState(null);

  const [leadEmail, setLeadEmail] = useState('');
  const [leadLoading, setLeadLoading] = useState(false);
  const [leadSuccess, setLeadSuccess] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [loading, user, navigate]);


  const submitLead = async () => {
    if (!leadEmail.trim()) return;

    try {
      setLeadLoading(true);

      const backendUrl =
        process.env.REACT_APP_BACKEND_URL ||
        (typeof window !== 'undefined' && window.location.hostname === 'localhost'
          ? 'http://localhost:8001'
          : '');

      const response = await fetch(
        `${backendUrl}/api/marketing/lead`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: leadEmail,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Erro ao cadastrar lead');
      }

      setLeadSuccess(true);
      setLeadEmail('');
    } catch (error) {
      console.error(error);
      alert('Erro ao cadastrar. Tente novamente.');
    } finally {
      setLeadLoading(false);
    }
  };

  const features = [
    {
      icon: <Users className="text-indigo-600" size={24} />,
      title: "Gestão de Pacientes",
      desc: "Cadastro completo, histórico centralizado, dados do responsável legal e acesso rápido ao prontuário do paciente.",
    },
    {
      icon: <ClipboardList className="text-indigo-600" size={24} />,
      title: "Prontuário & Odontograma",
      desc: "Evolução clínica, odontograma interativo e geração de insights com IA para apoiar a organização das informações clínicas.",
    },
    {
      icon: <Stethoscope className="text-indigo-600" size={24} />,
      title: "Anamnese Inteligente",
      desc: "Templates editáveis, link público para o paciente preencher, extração de dados e alertas com apoio de IA.",
    },
    {
      icon: <FileText className="text-indigo-600" size={24} />,
      title: "Contratos e Documentos Clínicos",
      desc: "Modelos inspirados nas diretrizes do CFO, com documentos auditáveis, validação por QR Code, link público e assinatura remota.",
    },
    {
      icon: <ShieldCheck className="text-indigo-600" size={24} />,
      title: "Boas Práticas e Conformidade",
      desc: "Adequação guiada à RDC Anvisa nº 1.002/2025, com documentos, esterilização, equipamentos, capacitações, rotinas, lembretes e resumo para fiscalização.",
    },
    {
      icon: <Calendar className="text-indigo-600" size={24} />,
      title: "Agenda Integrada",
      desc: "Controle de horários, lembretes e organização da rotina da clínica em poucos cliques.",
    },
    {
      icon: <Brain className="text-indigo-600" size={24} />,
      title: "Suporte com IA",
      desc: "Assistente inteligente para orientar o uso da plataforma e apoiar a produtividade da clínica no dia a dia.",
    },
    {
      icon: <Image className="text-indigo-600" size={24} />,
      title: "Imagens Clínicas",
      desc: "Organize fotos, exames, documentos e arquivos digitais dentro do prontuário do paciente.",
    },
    {
      icon: <MessageCircle className="text-indigo-600" size={24} />,
      title: "WhatsApp Business",
      desc: "Módulo adicional para comunicações automatizadas, como confirmações de agendamento e lembretes, mediante contratação específica.",
    },
  ];

  return (
    <div className="min-h-screen bg-white font-sans text-gray-900 overflow-x-hidden">

      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-lg border-b border-gray-100">
        <div className="mx-auto max-w-7xl px-4 h-28 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img
              src="/marketing/logo_official.png"
              alt="OdontoAssist AI Logo"
              className="h-28 sm:h-32 w-auto object-contain transition-transform hover:scale-105"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-8 text-sm font-semibold text-gray-500">
            <a href="#recursos" className="hover:text-indigo-600 transition-colors">Recursos</a>
            <a href="#diagnostico-2026" className="hover:text-indigo-600 transition-colors">Diagnóstico 2026</a>
            <a href="#programa-beneficios" className="hover:text-indigo-600 transition-colors">Programa de Benefícios</a>
            <a href="#precos" className="hover:text-indigo-600 transition-colors">Preços</a>
            <a href="#faq" className="hover:text-indigo-600 transition-colors">FAQ</a>
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/instalar"
              className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-indigo-200 text-indigo-700 font-semibold text-sm hover:bg-indigo-50 transition-all"
            >
              <Download size={16} />
              Instalar app
            </Link>

            <Link
              to="/login"
              className="hidden sm:inline-flex font-semibold text-gray-500 hover:text-indigo-600 transition-colors"
            >
              Entrar
            </Link>

            <Link
              to="/login?trial=1"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-indigo-600 text-white font-bold text-sm hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 hover:scale-105 active:scale-95"
            >
              Começar agora
            </Link>
          </div>
        </div>
      </header>

      <section className="relative pt-16 pb-24 lg:pt-24 lg:pb-32 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full -z-10 pointer-events-none">
          <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] bg-indigo-50 rounded-full blur-3xl opacity-60" />
          <div className="absolute bottom-[10%] left-[-5%] w-[400px] h-[400px] bg-slate-50 rounded-full blur-3xl opacity-60" />
        </div>

        <div className="mx-auto max-w-7xl px-4 grid lg:grid-cols-2 gap-16 items-center">
          <div className="max-w-2xl">
            <div className="inline-flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 px-3 py-1.5 rounded-full text-xs font-bold bg-indigo-50 text-indigo-700 mb-6 uppercase tracking-wider border border-indigo-100">
              <span className="inline-flex items-center gap-2">
                <Clock size={14} />
                7 dias de teste gratuito
              </span>

              <span className="hidden sm:inline text-indigo-300">•</span>

              <span>Sem cartão no início</span>
            </div>
              <h1 className="text-4xl sm:text-6xl font-black leading-[1.1] text-gray-900 mb-6">
                Organize toda a sua clínica em{' '}
                <span className="text-indigo-600">
                  uma única plataforma.
                </span>
              </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-6">
              Prontuário, agenda, odontograma, documentos, imagens, IA e comunicação
              com pacientes em uma plataforma criada para a rotina do
              cirurgião-dentista.
            </p>

            <div className="flex flex-wrap gap-3 mb-10">
              {[
                "7 dias grátis",
                "90 dias para recém-formados",
                "Sem cartão de crédito",
                "Cancele quando quiser"
              ].map((item, index) => (
                <div
                  key={index}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-indigo-100 shadow-sm text-sm font-semibold text-gray-700"
                >
                  <CheckCircle2 size={16} className="text-indigo-600" />
                  {item}
                </div>
              ))}
            </div>


            <div className="mb-8">
              <p className="text-sm font-semibold text-gray-700 mb-3">
                Ainda avaliando? Receba novidades, novos recursos e condições especiais.
              </p>

              {!leadSuccess ? (
                <div className="flex flex-col sm:flex-row gap-3 max-w-xl">
                  <input
                    type="email"
                    value={leadEmail}
                    onChange={(e) => setLeadEmail(e.target.value)}
                    placeholder="Seu melhor e-mail"
                    className="flex-1 px-5 py-4 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />

                  <button
                    type="button"
                    onClick={submitLead}
                    disabled={leadLoading}
                    className="px-6 py-4 rounded-2xl bg-white border-2 border-indigo-600 text-indigo-700 font-bold hover:bg-indigo-50"
                  >
                    {leadLoading
                      ? 'Enviando...'
                      : 'Quero ser avisado'}
                  </button>
                </div>
              ) : (
                <div className="inline-flex items-center px-5 py-4 rounded-2xl bg-green-50 text-green-700 font-semibold">
                  Cadastro realizado com sucesso.
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/login?trial=1"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-indigo-600 text-white font-bold text-lg hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-200 hover:-translate-y-1"
              >
                Criar conta gratuita <ArrowRight size={20} />
              </Link>
              <a
                href="#recursos"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-white border-2 border-gray-100 text-gray-700 font-bold text-lg hover:bg-gray-50 transition-all"
              >
                Conhecer Recursos
              </a>
            </div>

            <div className="mt-4">
              <Link
                to="/instalar"
                className="inline-flex items-center gap-2 text-indigo-700 font-semibold hover:text-indigo-800 transition-colors"
              >
                <Download size={18} />
                Saiba como instalar o app no celular
              </Link>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-sm font-semibold">
                <Sparkles size={16} />
                Primeiros acessos disponíveis
              </div>

              <p className="text-sm text-gray-500 font-medium leading-relaxed">
                Estamos liberando novas clínicas gradualmente para acompanhar de perto a evolução da plataforma e os feedbacks dos primeiros usuários.
              </p>
            </div>
          </div>

          <div className="relative">
            <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl border-8 border-white">
              <img
                src="/marketing/new/dentist_ia_1.png"
                alt="Dentista profissional"
                className="w-full h-auto object-cover aspect-[4/3]"
              />
            </div>
            <div className="absolute -top-6 -right-6 bg-white p-4 rounded-2xl shadow-xl border border-gray-50 z-20 animate-bounce-slow">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-50 rounded-full flex items-center justify-center text-indigo-600">
                  <CheckCircle2 size={20} />
                </div>
                <div>
                  <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Status</p>
                  <p className="text-sm font-bold text-gray-900">Clínica Organizada</p>
                </div>
              </div>
            </div>
            <Link
              to="/ajuda/tutoriais/programa-beneficios"
              className="absolute -bottom-10 -left-10 bg-white p-6 rounded-2xl shadow-xl border border-gray-50 z-20 hidden sm:block hover:-translate-y-1 transition-all"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center text-white">
                  <GraduationCap size={24} />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900">Programa de Benefícios</p>
                  <p className="text-xs text-gray-500">90 dias gratuitos para recém-formados</p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <section id="diagnostico-2026" className="border-y border-indigo-100 bg-gradient-to-br from-indigo-50 to-white py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4">
          <div className="grid items-center gap-10 lg:grid-cols-[1fr_420px]">
            <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-indigo-700 shadow-sm ring-1 ring-indigo-100">
              <ClipboardList size={15} /> Diagnóstico gratuito · cerca de 3 minutos
            </div>
            <h2 className="mt-5 max-w-3xl text-3xl font-black leading-tight text-gray-900 sm:text-4xl">
              Sua clínica está preparada para as novas regras de 2026?
            </h2>
            <p className="mt-4 max-w-3xl text-lg leading-8 text-gray-600">
              Em 2026, clínicas odontológicas passaram a lidar com novas referências para direitos do paciente, documentação clínica e Boas Práticas. O diagnóstico ajuda a identificar, em linguagem simples, o que já está organizado e o que merece atenção primeiro.
            </p>
            <div className="mt-6 flex flex-wrap gap-4 text-sm font-medium text-gray-700">
              {['Sem dados de pacientes', 'Resultado imediato', 'Prioridades personalizadas'].map((label) => (
                <span key={label} className="flex items-center gap-2"><CheckCircle2 size={17} className="text-emerald-600" />{label}</span>
              ))}
            </div>
            <Link to="/diagnostico-clinica-2026" className="mt-7 inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-7 py-4 text-lg font-bold text-white shadow-xl shadow-indigo-200 transition-all hover:-translate-y-1 hover:bg-indigo-700">
              Avaliar minha clínica <ArrowRight size={20} />
            </Link>
            <p className="mt-3 text-sm font-medium text-gray-600">Avaliação gratuita, sem cadastro inicial e sem compromisso.</p>
            </div>
            <div className="rounded-3xl bg-white p-7 shadow-xl shadow-indigo-100 ring-1 ring-indigo-100">
              <div className="flex items-center justify-between"><span className="text-sm font-semibold text-gray-600">Por que vale a pena responder</span><ShieldCheck className="text-indigo-600" size={26} /></div>
              <div className="mt-6 space-y-4">
                {[
                  ['Entenda sua situação', 'Veja o nível de preparação sem uma lista extensa ou linguagem complicada.'],
                  ['Evite começar pelo lugar errado', 'Receba primeiro os pontos que podem exigir mais atenção.'],
                  ['Conheça soluções práticas', 'Depois do resultado, veja como o OdontoAssist pode ajudar na organização diária.'],
                ].map(([title, desc], index) => (
                  <div key={title} className="flex gap-3 border-t border-gray-100 pt-4 first:border-0 first:pt-0"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-sm font-bold text-indigo-700">{index + 1}</span><div><h3 className="font-semibold text-gray-900">{title}</h3><p className="mt-0.5 text-sm text-gray-500">{desc}</p></div></div>
                ))}
              </div>
              <p className="mt-6 text-xs leading-5 text-gray-500">Ferramenta orientativa. O resultado não certifica conformidade e não substitui avaliação técnica.</p>
            </div>
          </div>

          <div className="mt-12">
            <div className="text-center">
              <span className="text-xs font-bold uppercase tracking-wide text-indigo-700">O que aconteceu em 2026</span>
              <h3 className="mt-2 text-2xl font-bold text-gray-900">Três frentes que se encontram na rotina da clínica</h3>
              <p className="mx-auto mt-3 max-w-3xl text-sm leading-6 text-gray-600">Elas não têm a mesma natureza nem o mesmo prazo, mas todas reforçam a necessidade de informação clara, prontuários consistentes, processos seguros e evidências organizadas.</p>
            </div>
            <div className="mt-7 grid gap-5 md:grid-cols-3">
              {[
                {
                  date: 'Em vigor desde 07/04/2026',
                  title: 'Estatuto dos Direitos do Paciente',
                  desc: 'A Lei nº 15.378/2026 reforçou informação acessível, consentimento, confidencialidade, segurança e acesso do paciente ao prontuário.',
                },
                {
                  date: 'Manual disponibilizado em março de 2026',
                  title: 'Prontuário do CFO',
                  desc: 'O CFO reuniu orientações e modelos para ficha clínica, plano de tratamento, evolução, TCLE, contratos, exames, imagens e entrega documental.',
                },
                {
                  date: 'Adequação geral até 11/12/2026*',
                  title: 'RDC Anvisa nº 1.002/2025',
                  desc: 'A norma detalha Boas Práticas para serviços odontológicos, incluindo estrutura, documentos, esterilização, equipamentos, equipe e segurança do paciente.',
                },
              ].map((item) => (
                <article key={item.title} className="rounded-2xl border border-indigo-100 bg-white p-6 shadow-sm">
                  <div className="flex items-center gap-2 text-xs font-bold text-indigo-700"><Clock size={15} />{item.date}</div>
                  <h4 className="mt-3 text-lg font-bold text-gray-900">{item.title}</h4>
                  <p className="mt-2 text-sm leading-6 text-gray-600">{item.desc}</p>
                </article>
              ))}
            </div>
            <p className="mt-4 text-xs leading-5 text-gray-500">*Prazo geral de 360 dias para adequação dos serviços existentes. Situações específicas, estrutura física e exigências complementares devem ser confirmadas com a Vigilância Sanitária local.</p>
          </div>
        </div>
      </section>

      <section id="programa-beneficios" className="py-24 bg-indigo-50/40 border-y border-indigo-100">
        <div className="mx-auto max-w-7xl px-4">
          <SectionTitle
            kicker="Programa de Benefícios"
            title="90 dias gratuitos para recém-formados"
            subtitle="Uma iniciativa do OdontoAssist AI para apoiar cirurgiões-dentistas nos primeiros passos da carreira."
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: <GraduationCap className="text-indigo-600" size={26} />,
                title: "Recém-formados",
                desc: "Para cirurgiões-dentistas que concluíram a graduação há até 12 meses."
              },
              {
                icon: <Clock className="text-indigo-600" size={26} />,
                title: "90 dias gratuitos",
                desc: "Benefício único, sem renovação, liberado após aprovação."
              },
              {
                icon: <FileText className="text-indigo-600" size={26} />,
                title: "Comprovação documental",
                desc: "Envie diploma, certificado ou declaração oficial de conclusão."
              },
              {
                icon: <CheckCircle2 className="text-indigo-600" size={26} />,
                title: "Sem cartão",
                desc: "Crie sua conta, solicite o benefício e acompanhe tudo pela plataforma."
              }
            ].map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-3xl border border-indigo-100 p-7 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all"
              >
                <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center mb-5">
                  {item.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/ajuda/tutoriais/programa-beneficios"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-indigo-600 text-white font-bold text-lg hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-200 hover:-translate-y-1"
            >
              Conhecer Programa de Benefícios
              <ArrowRight size={20} />
            </Link>

            <Link
              to="/login?trial=1"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-white border-2 border-indigo-100 text-indigo-700 font-bold text-lg hover:bg-indigo-50 transition-all"
            >
              Criar conta gratuita
            </Link>
          </div>

          <p className="mt-6 text-center text-sm text-gray-500">
            O benefício é concedido uma única vez por usuário, mediante análise do comprovante enviado.
          </p>
        </div>
      </section>

      <section id="recursos" className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4">
          <SectionTitle
            kicker="Funcionalidades"
            title="Tudo o que você precisa em uma única plataforma"
            subtitle="Desenvolvemos cada recurso pensando na rotina real do consultório, para que você foque no que realmente importa: o seu paciente."
          />

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((f, i) => (
              <div key={i} className="group p-8 rounded-3xl border border-gray-100 bg-white hover:border-indigo-200 hover:shadow-2xl hover:shadow-indigo-100 transition-all duration-300">
                <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  {f.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{f.title}</h3>
                <p className="text-gray-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="beneficios" className="py-24 bg-slate-50/50">
        <div className="mx-auto max-w-7xl px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <SectionTitle
                kicker="Benefícios"
                title="Mais tempo para cuidar dos pacientes. Menos tempo com burocracia."
                subtitle="O OdontoAssist AI ajuda sua clínica a organizar informações, reduzir retrabalho e profissionalizar a experiência do paciente."
                center={false}
              />
              <div className="space-y-8 mt-10">
                {[
                  {
                    icon: <Heart className="text-rose-500" />,
                    title: "Menos burocracia, mais atendimento",
                    desc: "Reduza retrabalho com anamnese digital, documentos padronizados e registros clínicos centralizados."
                  },
                  {
                    icon: <ShieldCheck className="text-indigo-600" />,
                    title: "Documentos auditáveis",
                    desc: "Contratos, termos e documentos clínicos com validação, QR Code, hash de integridade e histórico rastreável."
                  },
                  {
                    icon: <Sparkles className="text-indigo-400" />,
                    title: "IA para ganhar tempo",
                    desc: "Extraia dados da anamnese, gere alertas, organize informações do odontograma e conte com suporte inteligente."
                  }
                ].map((item, i) => (

                  <div key={i} className="flex gap-4">
                    <div className="mt-1 w-12 h-12 shrink-0 rounded-xl bg-white shadow-sm border border-gray-100 flex items-center justify-center">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">{item.title}</h4>
                      <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
                <img
                  src="/marketing/new/dentist_ia_2.png"
                  alt="Clínica moderna"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4">
          <SectionTitle
            kicker="Para quem é"
            title="Uma plataforma para diferentes fases da carreira odontológica"
            subtitle="Do início da carreira à rotina de clínicas consolidadas, o OdontoAssist AI foi pensado para simplificar a gestão odontológica."
          />

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              {
                icon: "🦷",
                title: "Clínicos gerais",
                desc: "Organização completa da rotina clínica."
              },
              {
                icon: "😁",
                title: "Especialistas",
                desc: "Documentos, prontuários e agenda centralizados."
              },
              {
                icon: "🎓",
                title: "Dentistas em início de carreira",
                desc: "Estrutura profissional desde os primeiros atendimentos."
              },
              {
                icon: "🚀",
                title: "Recém-formados",
                desc: "Apoio tecnológico nos primeiros passos da carreira."
              },
              {
                icon: "🏥",
                title: "Clínicas",
                desc: "Mais controle, segurança e produtividade."
              }
            ].map((item, index) => (
              <div
                key={index}
                className="bg-slate-50 rounded-3xl border border-gray-100 p-6 text-center hover:shadow-xl hover:-translate-y-1 transition-all"
              >
                <div className="text-4xl mb-4">
                  {item.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="mx-auto max-w-6xl px-4">
          <SectionTitle
            kicker="Depoimento real"
            title="Quem usa o OdontoAssist AI recomenda"
            subtitle="A experiência de quem participou dos primeiros meses de evolução da plataforma."
          />

          <div className="overflow-hidden rounded-[32px] border border-indigo-100 bg-gradient-to-br from-indigo-50 via-white to-purple-50 shadow-xl">
            <div className="grid lg:grid-cols-[380px_1fr] items-stretch">
              <div className="relative min-h-[420px]">
                <img
                  src="/marketing/ully-andrade.png"
                  alt="Dra. Ully Andrade, cirurgiã-dentista e usuária do OdontoAssist AI"
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-indigo-950/50 via-transparent to-transparent" />

                <div className="absolute left-6 bottom-6 text-white">
                  <p className="font-black text-xl">
                    Dra. Ully Andrade
                  </p>

                  <p className="text-sm text-indigo-100">
                    Cirurgiã-dentista
                  </p>
                </div>
              </div>

              <div className="p-8 sm:p-12 lg:p-16 flex flex-col justify-center">
                <div className="text-indigo-500 text-6xl font-black leading-none mb-4">
                  “
                </div>

                <blockquote className="text-xl sm:text-2xl text-gray-800 font-semibold leading-relaxed">
                  Sério, uma mão na roda. Consegui importar meus dados, inclusive
                  minha anamnese, e encontrei documentos e contratos prontos para
                  usar. O aplicativo é muito, muito completo.
                </blockquote>

                <div className="mt-8 flex flex-wrap gap-3">
                  {[
                    'Importação de dados',
                    'Anamnese digital',
                    'Documentos clínicos',
                    'Contratos digitais',
                  ].map((item) => (
                    <span
                      key={item}
                      className="inline-flex items-center gap-2 rounded-full bg-white border border-indigo-100 px-4 py-2 text-sm font-semibold text-indigo-700"
                    >
                      <CheckCircle2 size={15} />
                      {item}
                    </span>
                  ))}
                </div>

                <p className="mt-8 text-sm text-gray-500">
                  Depoimento espontâneo publicado pela profissional em seu perfil
                  no Instagram.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-indigo-950 text-white overflow-hidden relative">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-indigo-600/10 blur-[120px] rounded-full" />

        <div className="mx-auto max-w-7xl px-4">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div className="order-2 lg:order-1 relative">
              <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl shadow-black/50 border border-white/10">
                <img
                  src="/marketing/new/dentist_ia_3.png"
                  alt="Tecnologia na odontologia"
                  className="w-full h-auto"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-indigo-600 p-6 rounded-2xl shadow-xl z-20">
                <LayoutDashboard size={32} />
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <SectionTitle
                kicker="Tecnologia"
                title="Gestão visual e intuitiva do seu consultório"
                subtitle="Acompanhe o crescimento da sua clínica com indicadores claros e uma interface que não precisa de manual de instruções."
                center={false}
                dark={true}
              />
              <div className="space-y-6 mt-10">
                {[
                  "Anamnese com extração de dados e alertas por IA",
                  "Odontograma com geração de insights clínicos organizacionais",
                  "Contratos e documentos com validação por QR Code",
                  "Assinatura remota e histórico auditável dos documentos",
                  "Uso em computador, tablet e celular, com opção de instalação como aplicativo"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="w-6 h-6 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-400">
                      <CheckCircle2 size={16} />
                    </div>
                    <span className="text-lg font-medium text-indigo-50/90">{item}</span>
                  </div>
                ))}
              </div>
              <div className="mt-12 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/login?trial=1"
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-indigo-600 text-white font-bold text-lg hover:bg-indigo-500 transition-all shadow-xl shadow-indigo-900/20"
                >
                  Ver na prática <ArrowRight size={20} />
                </Link>

                <Link
                  to="/instalar"
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-white/10 text-white font-bold text-lg hover:bg-white/20 transition-all backdrop-blur-md border border-white/10"
                >
                  <Download size={20} />
                  Instalar app
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="como-funciona" className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4">
          <SectionTitle
            kicker="Passo a Passo"
            title="Começar é mais simples do que você imagina"
            subtitle="Em menos de 5 minutos você já terá sua clínica configurada e pronta para o futuro."
          />

          <div className="grid md:grid-cols-4 gap-10 relative">
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-gray-100 -translate-y-1/2 -z-10" />

            {[
              { n: "01", title: "Crie sua conta", desc: "Comece com 7 dias gratuitos sem informar cartão de crédito." },
              { n: "02", title: "Escolha o melhor caminho", desc: "Use o teste grátis ou solicite o Programa de Benefícios, caso seja elegível." },
              { n: "03", title: "Configure sua clínica", desc: "Cadastre profissionais, personalize modelos, logomarca, CRO e dados da clínica." },
              { n: "04", title: "Comece a atender", desc: "Cadastre pacientes e utilize prontuário, agenda, documentos e IA no dia a dia." },
            ].map((s, i) => (
              <div key={i} className="text-center bg-white px-6">
                <div className="w-16 h-16 rounded-full bg-white border-4 border-indigo-600 text-indigo-600 flex items-center justify-center text-2xl font-black mx-auto mb-6 shadow-lg">
                  {s.n}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{s.title}</h3>
                <p className="text-gray-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="precos" className="py-24 bg-indigo-50/30 border-y border-indigo-100">
        <div className="mx-auto max-w-7xl px-4">
          <SectionTitle
            kicker="Investimento"
            title="Preço justo para o seu crescimento"
            subtitle="Um plano completo, com teste gratuito e sem fidelidade."
          />

          <div className="max-w-md mx-auto relative">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-indigo-600 text-white px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest z-20">
              Mais Popular
            </div>
            <div className="bg-white rounded-3xl shadow-2xl border-2 border-indigo-600 p-10 text-center relative z-10 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50 rounded-bl-full -z-10" />

              <h3 className="text-2xl font-bold text-gray-900 mb-2">Plano Profissional</h3>
              <p className="text-gray-500 text-sm mb-8 font-medium">Tudo o que você precisa para sua clínica</p>

              <div className="mb-8">
                <div className="flex items-center justify-center">
                  <span className="text-2xl font-bold text-gray-900 mr-1">R$</span>
                  <span className="text-6xl font-black text-gray-900 tracking-tight">99,90</span>
                  <span className="text-gray-500 font-medium ml-2">/mês</span>
                </div>
                <p className="text-indigo-600 text-xs font-bold mt-2 italic">
                  Teste grátis por 7 dias. Sem fidelidade e sem cartão no início.
                </p>
              </div>

              <div className="space-y-4 mb-8 text-left">
                {[
                  "Pacientes ilimitados",
                  "Prontuários, odontograma e agenda",
                  "Imagens clínicas e documentos digitais",
                  "Anamnese digital com IA",
                  "Contratos e documentos clínicos auditáveis",
                  "Assinatura remota e validação por QR Code",
                  "Suporte com IA e central de ajuda",
                  "Módulo WhatsApp Business disponível como adicional"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="text-indigo-600" size={18} />
                    <span className="text-gray-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>

              <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-5 mb-8 text-left">
                <p className="text-sm font-bold text-indigo-900 mb-3">
                  Antes de assinar:
                </p>

                <div className="space-y-2">
                  {[
                    "Teste por 7 dias gratuitamente",
                    "Ou solicite 90 dias gratuitos, se você é recém-formado",
                    "Plano completo por R$ 99,90 por mês",
                    "Sem fidelidade e sem cartão no início"
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-2 text-sm text-indigo-900">
                      <CheckCircle2 className="text-indigo-600 mt-0.5 shrink-0" size={16} />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Link
                to="/login?trial=1"
                className="block w-full py-5 rounded-2xl bg-indigo-600 text-white font-black text-lg hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-200"
              >
                Começar Agora
              </Link>
              <div className="mt-5 grid grid-cols-2 gap-2 text-xs text-gray-500">
                <div className="flex items-center justify-center gap-1">
                  <CheckCircle2 size={14} className="text-indigo-600" />
                  7 dias grátis
                </div>

                <div className="flex items-center justify-center gap-1">
                  <CheckCircle2 size={14} className="text-indigo-600" />
                  Sem cartão no início
                </div>

                <div className="flex items-center justify-center gap-1">
                  <CheckCircle2 size={14} className="text-indigo-600" />
                  Sem fidelidade
                </div>

                <div className="flex items-center justify-center gap-1">
                  <CheckCircle2 size={14} className="text-indigo-600" />
                  Cancele quando quiser
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4">
          <div className="bg-white rounded-[40px] border border-gray-100 shadow-2xl overflow-hidden">
            <div className="grid lg:grid-cols-2">
              <div className="p-12 lg:p-20">
                <SectionTitle
                  kicker="Segurança"
                  title="Seus dados estão em boas mãos"
                  subtitle="Seguimos os mais altos padrões de segurança e criptografia para garantir que as informações dos seus pacientes estejam sempre protegidas."
                  center={false}
                />
                <div className="grid sm:grid-cols-2 gap-6 mt-10">
                  {[
                    { title: "Criptografia", desc: "Dados protegidos de ponta a ponta." },
                    { title: "Backups", desc: "Cópias de segurança diárias automáticas." },
                    { title: "Privacidade", desc: "100% em conformidade com a LGPD." },
                    { title: "Nuvem", desc: "Servidores de alta disponibilidade." },
                  ].map((item, i) => (
                    <div key={i}>
                      <h4 className="font-bold text-gray-900 mb-1">{item.title}</h4>
                      <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-indigo-600 flex items-center justify-center p-12 lg:p-20 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-full opacity-10">
                  <div className="absolute top-10 left-10 w-40 h-40 bg-white rounded-full blur-3xl" />
                  <div className="absolute bottom-10 right-10 w-60 h-60 bg-white rounded-full blur-3xl" />
                </div>
                <div className="relative z-10 bg-white/10 backdrop-blur-xl p-10 rounded-3xl border border-white/20">
                  <ShieldCheck className="text-white" size={120} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-white" aria-labelledby="google-calendar-title">
        <div className="mx-auto max-w-5xl px-4">
          <div className="rounded-[32px] border border-indigo-100 bg-indigo-50/60 p-8 lg:p-12">
            <div className="flex flex-col lg:flex-row gap-8 lg:items-start">
              <div className="shrink-0 w-14 h-14 rounded-2xl bg-indigo-600 text-white flex items-center justify-center shadow-lg shadow-indigo-200">
                <Calendar size={28} aria-hidden="true" />
              </div>
              <div>
                <p className="text-sm font-bold uppercase tracking-wider text-indigo-600 mb-2">
                  Integração opcional
                </p>
                <h2 id="google-calendar-title" className="text-3xl lg:text-4xl font-black text-gray-900 mb-5">
                  Seus agendamentos também no Google Calendar
                </h2>
                <div className="space-y-4 text-gray-600 leading-relaxed text-justify">
                  <p>
                    Cada profissional pode conectar voluntariamente sua própria Conta Google. O OdontoAssist AI
                    cria no Google Calendar os eventos correspondentes aos seus agendamentos e mantém o mesmo
                    evento atualizado quando data, horário, duração ou outras informações do atendimento são alteradas.
                  </p>
                  <p>
                    Quando um agendamento é cancelado ou excluído, o evento correspondente também pode ser removido.
                    A sincronização atual é unidirecional: compromissos pessoais do Google não são importados para o
                    OdontoAssist AI. A conexão pode ser interrompida pelo usuário a qualquer momento.
                  </p>
                </div>
                <div className="flex flex-wrap gap-4 mt-6 text-sm font-bold">
                  <Link to="/privacidade" className="text-indigo-700 hover:text-indigo-900 hover:underline">
                    Política de Privacidade
                  </Link>
                  <Link to="/termos-de-uso" className="text-indigo-700 hover:text-indigo-900 hover:underline">
                    Termos de Uso
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="faq" className="py-24 lg:py-32 bg-slate-50/50">
        <div className="mx-auto max-w-3xl px-4">
          <SectionTitle
            kicker="Dúvidas"
            title="Perguntas Frequentes"
            subtitle="Tire suas principais dúvidas sobre o OdontoAssist AI."
          />

          <div className="space-y-4 mt-12">
            {[
              {
                q: "O teste grátis precisa de cartão?",
                a: "Não. Você pode criar sua conta e usar os recursos principais por 7 dias sem informar cartão de crédito."
              },
              {
                q: "Existe algum benefício gratuito por mais tempo?",
                a: "Sim. Cirurgiões-dentistas formados há até 12 meses podem solicitar o Programa de Benefícios e receber 90 dias gratuitos após análise da documentação."
              },
              {
                q: "Quem pode solicitar o Programa de Benefícios?",
                a: "O programa é destinado exclusivamente a cirurgiões-dentistas que concluíram a graduação há até 12 meses, mediante comprovação documental."
              },
              {
                q: "O Programa de Benefícios pode ser renovado?",
                a: "Não. O benefício é concedido uma única vez por usuário, com validade de 90 dias."
              },
              {
                q: "Posso usar no meu celular ou tablet?",
                a: "Sim. O OdontoAssist AI é responsivo e pode ser utilizado em computadores, tablets e celulares. Também pode ser instalado como aplicativo."
              },
              {
                q: "Qual é o valor da assinatura?",
                a: "O Plano OdontoAssist AI custa R$ 99,90 por mês. Você pode testar gratuitamente por 7 dias, sem informar cartão no início e sem fidelidade."
              },
              {
                q: "O Módulo WhatsApp Business está incluído no plano?",
                a: "Não. O Módulo WhatsApp Business é um módulo adicional, contratado separadamente, para clínicas que desejam automatizar comunicações como confirmações e lembretes de agendamento."
              },              
              {
                q: "Meus dados antigos podem ser importados?",
                a: "Sim, oferecemos suporte para importação de planilhas de pacientes. Entre em contato com nosso suporte após criar sua conta."
              },
              {
                q: "Os documentos seguem o Manual do Prontuário do Paciente em Odontologia?",
                a: "Os modelos foram estruturados com base nas diretrizes do CFO, com foco em organização, rastreabilidade e segurança documental. O cirurgião-dentista pode revisar, editar e adaptar os textos conforme sua realidade clínica e orientação profissional."
              },
              {
                q: "A IA substitui a análise do dentista?",
                a: "Não. A IA atua como apoio para organização, extração de dados, alertas e geração de insights. A decisão clínica e a responsabilidade profissional continuam sendo sempre do cirurgião-dentista."
              },
              {
                q: "É difícil aprender a usar?",
                a: "Não. A plataforma foi desenhada para ser intuitiva, com tutoriais e suporte com IA para orientar o uso no dia a dia."
              },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden transition-all">
                <button
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                  onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                >
                  <span className="font-bold text-gray-900">{item.q}</span>
                  <div className={`text-indigo-600 transition-transform ${faqOpen === i ? "rotate-180" : ""}`}>
                    <ChevronDown size={20} />
                  </div>
                </button>
                {faqOpen === i && (
                  <div className="px-6 pb-6 text-gray-600 leading-relaxed animate-fade-in">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="mx-auto max-w-7xl px-4">
          <div className="relative rounded-[40px] bg-indigo-950 p-12 lg:p-24 text-center overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full -z-10">
              <div className="absolute top-[-20%] left-[-10%] w-[500px] h-[500px] bg-indigo-600/20 rounded-full blur-[100px]" />
              <div className="absolute bottom-[-20%] right-[-10%] w-[500px] h-[500px] bg-slate-600/20 rounded-full blur-[100px]" />
            </div>

            <h2 className="text-4xl lg:text-6xl font-black text-white mb-8 leading-tight">
              Simplifique a rotina da sua clínica a partir de hoje.
            </h2>
            <p className="text-xl text-indigo-50/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              Crie sua conta sem cartão, teste gratuitamente por 7 dias e conheça o OdontoAssist AI na prática.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/login?trial=1"
                className="px-10 py-5 rounded-2xl bg-indigo-600 text-white font-black text-xl hover:bg-indigo-700 transition-all shadow-2xl shadow-indigo-900/40 hover:-translate-y-1"
              >
                Criar conta gratuita
              </Link>
              <Link
                to="/instalar"
                className="px-10 py-5 rounded-2xl bg-white/10 text-white font-black text-xl hover:bg-white/20 transition-all backdrop-blur-md border border-white/10 inline-flex items-center justify-center gap-2"
              >
                <Download size={20} />
                Instalar app
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-white border-t border-gray-100 pt-20 pb-10">
        <div className="mx-auto max-w-7xl px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12 mb-16">
            <div className="col-span-2 md:col-span-1">
              <Link to="/" className="flex items-center mb-6">
                <img
                  src="/marketing/logo_official.png"
                  alt="OdontoAssist AI Logo"
                  className="h-28 w-auto object-contain"
                />
              </Link>
              <p className="text-gray-500 text-sm leading-relaxed">
                A plataforma de gestão inteligente que humaniza o atendimento odontológico através da tecnologia.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-gray-900 mb-6">Produto</h4>
              <ul className="space-y-4 text-sm text-gray-500 font-medium">
                <li><a href="#recursos" className="hover:text-indigo-600 transition-colors">Recursos</a></li>
                <li><a href="#programa-beneficios" className="hover:text-indigo-600 transition-colors">Programa de Benefícios</a></li>
                <li><a href="#precos" className="hover:text-indigo-600 transition-colors">Preços</a></li>
                <li><Link to="/login?trial=1" className="hover:text-indigo-600 transition-colors">Teste Grátis</Link></li>
                <li><Link to="/instalar" className="hover:text-indigo-600 transition-colors">Instalar app</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-gray-900 mb-6">Suporte</h4>
              <ul className="space-y-4 text-sm text-gray-500 font-medium">
                <li><a href="#faq" className="hover:text-indigo-600 transition-colors">FAQ</a></li>
                <li><Link to="/ajuda" className="hover:text-indigo-600 transition-colors">Central de Ajuda</Link></li>
                <li><Link to="/contato" className="hover:text-indigo-600 transition-colors">Contato</Link></li>
                <li><Link to="/status" className="hover:text-indigo-600 transition-colors">Status do Sistema</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-gray-900 mb-6">Legal</h4>
              <ul className="space-y-4 text-sm text-gray-500 font-medium">
                <li><Link to="/privacidade" className="hover:text-indigo-600 transition-colors">Privacidade</Link></li>
                <li><Link to="/termos-de-uso" className="hover:text-indigo-600 transition-colors">Termos de Uso</Link></li>
                <li><Link to="/lgpd" className="hover:text-indigo-600 transition-colors">LGPD</Link></li>
                <li><Link to="/seguranca" className="hover:text-indigo-600 transition-colors">Segurança</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-100 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-xs font-medium">
              © {new Date().getFullYear()} OdontoAssist AI. Todos os direitos reservados.
            </p>
            <div className="flex items-center gap-6">
              <a href="#" className="text-gray-400 hover:text-indigo-600 transition-colors">Instagram</a>
              <a href="#" className="text-gray-400 hover:text-indigo-600 transition-colors">LinkedIn</a>
              <a href="#" className="text-gray-400 hover:text-indigo-600 transition-colors">YouTube</a>
            </div>
          </div>
        </div>
      </footer>

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 3s ease-in-out infinite;
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out forwards;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}} />
    </div>
  );
}
